import { Autocomplete, AutocompleteChangeReason, Chip, TextField } from "@mui/material"
import { SyntheticEvent, useMemo } from "react"

export type TagOption = {
  id?: string
  name: string
  isNew?: boolean
}

type TagMultiSelectProps = {
  label: string
  value: TagOption[]
  options: TagOption[]
  onChange: (value: TagOption[]) => void
  placeholder?: string
  helperText?: string
  error?: boolean
  required?: boolean
}

const normalizeName = (name: string) => name.trim()

export const TagMultiSelect = ({
  label,
  value,
  options,
  onChange,
  placeholder,
  helperText,
  error,
  required,
}: TagMultiSelectProps) => {
  const normalized = useMemo(() => {
    const map = new Map<string, TagOption>()
    options.forEach((opt) => {
      const key = normalizeName(opt.name).toLowerCase()
      if (!map.has(key)) {
        map.set(key, opt)
      }
    })
    return map
  }, [options])

  const handleChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: (TagOption | string)[],
    _reason: AutocompleteChangeReason
  ) => {
    const unique: TagOption[] = []
    const seen = new Set<string>()

    newValue.forEach((entry) => {
      const candidate = typeof entry === "string" ? { name: entry.trim(), isNew: true } : entry
      const normalizedName = normalizeName(candidate.name)
      if (!normalizedName) {
        return
      }
      const key = normalizedName.toLowerCase()
      if (seen.has(key)) {
        return
      }
      const existing = normalized.get(key)
      unique.push(existing ? existing : { ...candidate, name: normalizedName })
      seen.add(key)
    })

    onChange(unique)
  }

  return (
    <Autocomplete<TagOption, true, false, true>
      multiple
      freeSolo
      options={options}
      value={value}
      onChange={handleChange}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, index) => {
          const option = typeof opt === "string" ? { name: opt, isNew: true } : opt
          return (
            <Chip
              {...getTagProps({ index })}
              key={`${option.id || "new"}-${option.name}`}
              label={option.name}
              color={option.isNew ? "secondary" : "default"}
              size="small"
            />
          )
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          error={error}
          required={required}
        />
      )}
      isOptionEqualToValue={(option, selected) => {
        if (option == null || selected == null) {
          return false
        }

        const optAny: any = option as any
        const selAny: any = selected as any

        const optionId: string | undefined = typeof optAny === "string" ? undefined : optAny?.id
        const selectedId: string | undefined = typeof selAny === "string" ? undefined : selAny?.id

        if (optionId && selectedId) {
          return optionId === selectedId
        }

        const optionName: string | undefined = typeof optAny === "string" ? optAny : optAny?.name
        const selectedName: string | undefined = typeof selAny === "string" ? selAny : selAny?.name

        if (!optionName || !selectedName) {
          return false
        }

        return optionName.toLowerCase() === selectedName.toLowerCase()
      }}
      filterOptions={(opts, params) => {
        const filtered = opts.filter((opt) =>
          opt.name.toLowerCase().includes(params.inputValue.toLowerCase())
        )
        const normalizedInput = normalizeName(params.inputValue)
        const exists = normalizedInput && normalized.has(normalizedInput.toLowerCase())
        if (normalizedInput !== "" && !exists) {
          filtered.push({ name: normalizedInput, isNew: true })
        }
        return filtered
      }}
      handleHomeEndKeys
      clearOnBlur
      selectOnFocus
    />
  )
}

export default TagMultiSelect
